import React from "react";
import styles from "./PDFExport.module.scss";
import { PesTest, TrainedIndicator } from "generated/graphql";
import { PartialDeep } from "type-fest";
import { format, parseISO } from "date-fns";
import { GetDynamicValue, GetPESIndex } from "@pages/pdf-export/Indices";
import { IndicatorGradientColors } from "@pages/pdf-export/PDFExport";
import type { PdfIndicatorHeader } from "@pages/pdf-export/Page1Table";
import tinygradient from "tinygradient";

interface Page2TableProps {
  allTests: PartialDeep<PesTest, { recurseIntoArrays: true }>[];
  indicatorNamesAndIds: {
    id: string;
    name: string;
    displayName: string;
    trainedIndicator: PartialDeep<TrainedIndicator>;
  }[];
  /** Nitrogen headers to render; defaults to all of them. */
  nitrogenHeaders?: PdfIndicatorHeader[];
  /** Soil texture headers to render; defaults to all of them. */
  soilTextureHeaders?: PdfIndicatorHeader[];
}

// Nitrogen section headers
export const page2NitrogenHeaders: PdfIndicatorHeader[] = [
  {
    id: "extractableNitrates",
    displayName: "Extractable Nitrates",
    name: "Nitrate nitrogen",
  },
  {
    id: "extractableAmmonium",
    displayName: "Extractable Ammonium",
    name: "Ammonium nitrogen",
  },
];

// Soil Texture section headers
export const page2SoilTextureHeaders: PdfIndicatorHeader[] = [
  { id: "sand", displayName: "Sand", name: "Sand 2.00-0.063mm" },
  { id: "silt", displayName: "Silt", name: "Silt 0.063-0.002mm" },
  { id: "clay", displayName: "Clay", name: "Clay <0.002mm" },
];

const Page2Table: React.FC<Page2TableProps> = ({
  allTests,
  indicatorNamesAndIds,
  nitrogenHeaders = page2NitrogenHeaders,
  soilTextureHeaders = page2SoilTextureHeaders,
}) => {
  const gradient = tinygradient(IndicatorGradientColors);

  return (
    <div className={styles.reportTableContainer}>
      <div className={styles.tableWrapper}>
        <table className={styles.infoTable}>
          <thead>
            <tr>
              {/* Test Info Block Headers */}
              <th rowSpan={2} className={styles.tableHeader}>
                Sample Name
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Farm
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Field
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                What3Words
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Date of Test
              </th>

              {/* Nitrogen Section Header */}
              {nitrogenHeaders.map((header) => (
                <th key={header.id} className={styles.subHeader} colSpan={2}>
                  {header.displayName}
                </th>
              ))}

              {/* Soil Texture Section Header */}
              {soilTextureHeaders.map((header) => (
                <th key={header.id} className={styles.subHeader}>
                  {header.displayName}
                </th>
              ))}
            </tr>
            <tr>
              {/* Nitrogen Subheaders */}
              {nitrogenHeaders.map((header) => (
                <React.Fragment key={header.id}>
                  <th key={`${header.id}-value`} className={styles.subHeader}>
                    Value in mg/kg
                  </th>
                  <th key={`${header.id}-index`} className={styles.subHeader}>
                    PES Index
                  </th>
                </React.Fragment>
              ))}

              {/* Soil Texture Subheaders */}
              {soilTextureHeaders.map((header) => (
                <th key={`${header.id}-value`} className={styles.subHeader}>
                  Value in %
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allTests.map((test, index) => (
              <tr key={test.id} className={styles.tableRow}>
                {/* Render the Test Info Block */}
                <td className={styles.tableCell}>{test.name}</td>
                <td className={styles.tableCell}>{test.farm?.name}</td>
                <td className={styles.tableCell}>{test.field?.name}</td>
                <td className={`${styles.tableCell} ${styles.noBreak}`}>
                  {test.location
                    ? `${test.location.what3Words?.words ?? "Unknown"}`
                    : "N/A"}
                </td>
                <td className={styles.tableCell}>
                  {test.createdAt
                    ? format(parseISO(test.createdAt), "dd/MM/yyyy")
                    : "N/A"}
                </td>

                {/* Render Nitrogen Values */}

                {nitrogenHeaders.map((header) => {
                  const { value } = GetDynamicValue(test, header.name);

                  const trainedIndicator = indicatorNamesAndIds.find(
                    (i) => i.name === header.name
                  )?.trainedIndicator;
                  const pesIndex = GetPESIndex(value, trainedIndicator);
                  const index = pesIndex
                    ? pesIndex.indices.names[pesIndex.indexValue]
                    : "N/A";
                  const divisor = pesIndex
                    ? pesIndex.indices.names.length - 1
                    : 0;
                  const gradientPosition = Math.min(
                    Math.max(
                      0,
                      divisor === 0 || !pesIndex
                        ? 0
                        : pesIndex.indexValue / divisor
                    ),
                    1
                  );
                  const indexColor =
                    index === "N/A"
                      ? undefined
                      : gradient
                          .rgbAt(gradientPosition)
                          .setAlpha(0.7)
                          .toHex8String();

                  return (
                    <React.Fragment key={header.id}>
                      <td
                        key={`${header.id}-value`}
                        className={styles.tableCell}
                      >
                        {typeof value === "number"
                          ? value.toFixed(
                              trainedIndicator?.indicatorDefinition
                                ?.displayDecimalPlaces ?? 2
                            )
                          : value}
                      </td>
                      <td
                        key={`${header.id}-index`}
                        className={`${styles.tableCell} ${
                          indexColor &&
                          !gradient.rgbAt(gradientPosition).isLight()
                            ? styles.light
                            : ""
                        }`}
                        style={{
                          backgroundColor: indexColor,
                        }}
                      >
                        {index}
                      </td>
                    </React.Fragment>
                  );
                })}

                {/* Render Soil Texture Values */}
                {soilTextureHeaders.map((header) => {
                  const { value } = GetDynamicValue(test, header.name);
                  const trainedIndicator = indicatorNamesAndIds.find(
                    (i) => i.name === header.name
                  )?.trainedIndicator;

                  return (
                    <React.Fragment key={header.id}>
                      <td
                        key={`${header.id}-value`}
                        className={styles.tableCell}
                      >
                        {test.isFailure
                          ? "N/A"
                          : typeof value === "number"
                            ? value.toFixed(
                                trainedIndicator?.indicatorDefinition
                                  ?.displayDecimalPlaces ?? 2
                              )
                            : value}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Page2Table;
